import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { get } from "lodash";
import { Portal, PortalProps } from "react-portal";
import { PortalWithBoundingRect } from "@sonder/ui/source/styled/components/PortalWithBoundingRect";
import {
  PortalRenderFn,
  PortalWithTrigger,
} from "@sonder/ui/source/styled/components/PortalWithTrigger";

const DropdownWrapper = styled.div`
  display: inline-block;
  position: relative;
`;

type PortalComponent = React.Component<PortalProps>;
const getNode = (portal: PortalComponent | null): HTMLElement | null =>
  get(portal, "props.node") || get(portal, "defaultNode") || null;

export enum DropdownSource {
  QuickView = "QUICK_VIEW",
}
interface DropdownProps {
  boundingRect?: Omit<DOMRect, "toJSON"> | null;
  children: PortalRenderFn;
  className?: string;
  isOpen?: boolean;
  onClose?(): void;
  onOpen?(): void;
  source?: DropdownSource;
  trigger?: JSX.Element;
}

const PortalContext = createContext<PortalComponent | null>(null);

const useEffectOnce = (cb: () => void) => {
  const didRun = useRef(false);
  useEffect(() => {
    if (!didRun.current) {
      cb();
      didRun.current = true;
    }
  });
};

export const Dropdown = ({
  boundingRect,
  children,
  className,
  isOpen: isInitiallyOpen,
  onClose = () => undefined,
  onOpen = () => undefined,
  source,
  trigger,
}: DropdownProps) => {
  const [, setIsRendered] = useState(false);
  const [isOpen, setIsOpen] = useState(isInitiallyOpen || false);
  const portalParent = useContext(PortalContext);
  const portalNode = useRef<PortalComponent>(null);
  const portalParentNode = getNode(portalParent);
  const openPortal = useCallback(() => {
    setIsOpen(true);
    if (onOpen) {
      onOpen();
    }
  }, [onOpen]);
  const closePortal = useCallback(() => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);
  const onKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (isOpen && e.key === "Escape") {
        closePortal();
      }
    },
    [closePortal, isOpen],
  );
  const onOutsideClick = useCallback(
    (e: MouseEvent) => {
      const rootNode = getNode(portalNode.current);
      if (
        !rootNode ||
        rootNode.contains(e.target as HTMLElement) ||
        (e.button && e.button !== 0)
      ) {
        return;
      }
      if (isOpen) {
        if (source === DropdownSource.QuickView) {
          setTimeout(() => closePortal(), 0);
        } else {
          closePortal();
        }
      }
    },
    [closePortal, isOpen, portalNode.current],
  );
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keyup", onKeyUp);
      document.addEventListener("click", onOutsideClick);
    }
    return () => {
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("click", onOutsideClick);
    };
  }, [isOpen, onKeyUp, onOutsideClick]);
  /**
   * There is a bug with our implementation. The first render of the Dropdown
   * will be missing the portalNode ref, and if there is no subsequent render,
   * Dropdowns inside of other Dropdowns will close whenever anything inside of them is clicked.
   *
   * This hack ensures that there is always a second render, slightly bad for performance,
   * but much better in terms of UX.
   */
  useEffectOnce(() => setIsRendered(true));
  const portalFunctionParams = {
    isOpen,
    closePortal,
    openPortal,
    portal: (portalChildren: React.ReactNode) =>
      isOpen ? (
        <Portal node={portalParentNode} ref={portalNode}>
          <PortalContext.Provider value={portalNode.current}>
            {portalChildren}
          </PortalContext.Provider>
        </Portal>
      ) : (
        <React.Fragment />
      ),
  };
  const wrappedChildren = isOpen && children({ ...portalFunctionParams });
  return (
    <DropdownWrapper className={className}>
      {!boundingRect && trigger && (
        <PortalWithTrigger
          {...portalFunctionParams}
          className={className}
          trigger={trigger}
        >
          {wrappedChildren}
        </PortalWithTrigger>
      )}
      {boundingRect && !trigger && (
        <PortalWithBoundingRect
          {...portalFunctionParams}
          boundingRect={boundingRect}
          className={className}
        >
          {wrappedChildren}
        </PortalWithBoundingRect>
      )}
    </DropdownWrapper>
  );
};
